import { db as supabase } from '../lib/dbClient';
import { pointsService } from './pointsService';
import { 
  EventType, 
  MilestoneType, 
  Missao, 
  MissaoEvento, 
  MissaoUser, 
  MissaoUserEvento,
  Medalha,
  Conquista
} from '@/types';

export const gamificationService = {
  /**
   * Função principal solicitada pelo usuário para processar qualquer evento de gamificação.
   * Centraliza a lógica de missões, medalhas e conquistas.
   */
  async processarEventoUsuario(userId: string, tipoEvento: EventType, metadata: { 
    materialId?: string | null,
    materialName?: string | null,
    materialCode?: string | null,
    category?: string | null,
    ecopontoId?: string | null, 
    conteudoEducacionalId?: string | null, 
    quizId?: string | null, 
    amount?: number,
    weightKg?: number,
    materialIds?: string[],
    [key: string]: any
  }) {
    console.log(`[Gamification] Processando evento ${tipoEvento} para usuário ${userId}`, metadata);
    
    try {
      // 1. Processar Missões e Eventos de Missão
      await this.processMissions(userId, tipoEvento, metadata);
      
      // 2. Processar Conquistas Globais (Marcos)
      // Tipos de conquistas baseadas no evento
      if (tipoEvento === 'DESCARTE' || tipoEvento === 'MATERIAL') {
        await this.processAchievements(userId, 'DESCARTES');
        // VOLUME e PONTOS são verificados no processAchievements baseado no perfil atualizado
      }
      
      await this.processAchievements(userId, 'PONTOS');
      await this.processAchievements(userId, 'VOLUME');
      
    } catch (error) {
      console.error('[Gamification] Erro ao processar evento:', error);
    }
  },

  /**
   * Gerencia o progresso das missões e seus eventos associados.
   */
  async processMissions(userId: string, tipoEvento: EventType, metadata: any) {
    const now = new Date();
    
    // 1. Sync statuses first to ensure we process active missions
    try {
      const { data: missionsToSync } = await supabase
        .from('missoes')
        .select('id, status, data_inicio, data_fim')
        .in('status', ['Programada', 'Ativa', 'ATIVA', 'Ativo', 'ATIVO']);

      if (missionsToSync) {
        for (const m of missionsToSync) {
          let newStatus = m.status;
          const start = m.data_inicio ? new Date(`${m.data_inicio.split('T')[0]}T00:00:00`) : null;
          const end = m.data_fim ? new Date(`${m.data_fim.split('T')[0]}T23:59:59.999`) : null;

          if (m.status === 'Programada') {
            if ((!start || now >= start) && (!end || now <= end)) {
              newStatus = 'Ativa';
            } else if (end && now > end) {
              newStatus = 'Encerrada';
            }
          } else if (m.status === 'Ativa' || m.status === 'ATIVA' || m.status === 'Ativo' || m.status === 'ATIVO') {
            if (end && now > end) {
              newStatus = 'Encerrada';
            }
          }

          if (newStatus !== m.status) {
            await supabase.from('missoes').update({ status: newStatus, data_atualizacao: now.toISOString() }).eq('id', m.id).catch(() => {});
          }
        }
      }
    } catch (err) {
      console.warn('[Gamification] Erro ao sincronizar status de missões:', err);
    }
    
    // 2. Buscar missões que estão Ativas
    const { data: activeMissions, error: missionsError } = await supabase
      .from('missoes')
      .select(`
        *,
        missoes_eventos (*)
      `)
      .or('status.eq.Ativa,status.eq.ATIVA,status.eq.Ativo,status.eq.ATIVO,status.is.null');

    if (missionsError || !activeMissions || activeMissions.length === 0) return;

    for (const mission of activeMissions) {
      // Check date validity
      const start = mission.data_inicio ? new Date(`${mission.data_inicio.split('T')[0]}T00:00:00`) : null;
      const end = mission.data_fim ? new Date(`${mission.data_fim.split('T')[0]}T23:59:59.999`) : null;
      if (start && start > now) continue;
      if (end && end < now) continue;

      // Buscar se a missão já iniciou para o usuário
      const { data: existingUserMission } = await supabase
        .from('missoes_user')
        .select('*')
        .eq('usuario_id', userId)
        .eq('missao_id', mission.id)
        .maybeSingle();

      let userMission = existingUserMission;

      if (!userMission) {
        // Iniciar missão para o usuário (Auto-enroll)
        const { data: newUserMission, error: umError } = await supabase
          .from('missoes_user')
          .insert({
            usuario_id: userId,
            missao_id: mission.id,
            nome_missao: mission.nome,
            exp: mission.exp || 0,
            pontos: mission.pontos || 0,
            cashback: mission.cashback || 0,
            progresso: 0,
            concluida: false
          })
          .select()
          .single();

        if (umError || !newUserMission) {
          console.error(`[Gamification] Falha ao auto-inscrever usuário ${userId} na missão ${mission.id}:`, umError);
          continue;
        }
        userMission = newUserMission;
      }

      if (userMission.concluida) continue;

      const events: MissaoEvento[] = mission.missoes_eventos || [];

      // Case A: Mission with no explicit sub-events in missoes_eventos (General challenge)
      if (events.length === 0) {
        if (tipoEvento === 'DESCARTE' || tipoEvento === 'MATERIAL') {
          await this.finalizeMission(userId, userMission.id, mission);
        }
        continue;
      }

      // Case B: Mission with sub-events
      const isDiscardFlow = tipoEvento === 'DESCARTE' || tipoEvento === 'MATERIAL';

      const matchingEvents = events.filter((event: MissaoEvento) => {
        const isEvDiscard = event.tipo_evento === 'DESCARTE' || event.tipo_evento === 'MATERIAL';

        if (event.tipo_evento !== tipoEvento && !(isDiscardFlow && isEvDiscard && !event.material_id)) {
          return false;
        }

        // Verificações específicas de ID se definidos no evento
        if (event.material_id) {
          const matMatch = 
            event.material_id === metadata.materialId || 
            event.material_id === metadata.materialCode ||
            event.material_id === metadata.materialName ||
            event.material_id === metadata.category ||
            (metadata.materialIds && Array.isArray(metadata.materialIds) && metadata.materialIds.includes(event.material_id));
          if (!matMatch) return false;
        }

        if (event.ecoponto_id) {
          const ecoMatch = 
            event.ecoponto_id === metadata.ecopontoId ||
            event.ecoponto_id === metadata.ecopointId;
          if (!ecoMatch) return false;
        }

        if (event.conteudo_educacional_id && event.conteudo_educacional_id !== metadata.conteudoEducacionalId) return false;
        if (event.quiz_id && event.quiz_id !== metadata.quizId) return false;

        return true;
      });

      if (matchingEvents.length === 0) continue;

      // Atualizar eventos individuais
      for (const event of matchingEvents) {
        const rawAmount = metadata.amount != null ? Number(metadata.amount) : (metadata.weightKg != null ? Number(metadata.weightKg) : 1);
        const metaTarget = Number(event.meta_quantidade) || 1;
        const increment = rawAmount > 0 ? rawAmount : 1;

        const { data: userEvent } = await supabase
          .from('missoes_user_eventos')
          .select('*')
          .eq('missao_user_id', userMission.id)
          .eq('evento_id', event.id)
          .maybeSingle();

        // Se já concluiu este evento específico, não incrementa mais nada
        if (userEvent?.concluido) continue;

        const currentProg = Number(userEvent?.progresso_atual) || 0;
        const novoProgresso = Number((currentProg + increment).toFixed(3));
        const concluido = novoProgresso >= (metaTarget - 0.0001);

        if (userEvent) {
          await supabase
            .from('missoes_user_eventos')
            .update({
              progresso_atual: concluido ? metaTarget : novoProgresso,
              concluido: concluido,
              data_conclusao: concluido ? new Date().toISOString() : null
            })
            .eq('id', userEvent.id);
        } else {
          await supabase
            .from('missoes_user_eventos')
            .insert({
              missao_user_id: userMission.id,
              evento_id: event.id,
              progresso_atual: concluido ? metaTarget : novoProgresso,
              concluido: concluido,
              data_conclusao: concluido ? new Date().toISOString() : null
            });
        }
      }

      // Verificar se a MISSÃO toda foi concluída
      await this.checkMissionCompletion(userId, userMission.id, mission);
    }
  },

  /**
   * Finaliza uma missão para o usuário concedendo todas as recompensas.
   */
  async finalizeMission(userId: string, userMissionId: string, mission: any) {
    const { data: updatedMission, error: updateError } = await supabase
      .from('missoes_user')
      .update({
        progresso: 1,
        concluida: true,
        data_conclusao: new Date().toISOString(),
        recompensa_recebida: true
      })
      .eq('id', userMissionId)
      .eq('concluida', false)
      .select()
      .single();

    if (!updateError && updatedMission) {
      console.log(`[Gamification] Usuário ${userId} completou a missão: "${mission.nome || updatedMission.nome_missao}"`);
      
      // 1. Conceder Medalha
      try {
        await this.grantMedal(userId, mission.id || updatedMission.missao_id);
      } catch (e) {
        console.error('[Gamification] Erro ao conceder medalha:', e);
      }

      // 2. Conceder Pontos e EXP
      try {
        await this.applyRewards(userId, mission.id ? mission : updatedMission);
      } catch (e) {
        console.error('[Gamification] Erro ao aplicar recompensas:', e);
      }

      // 3. Criar Notificação de Missão Concluída
      try {
        await supabase.from('notificacoes').insert({
          usuario_id: userId,
          titulo: 'Missão Cumprida! 🎯',
          mensagem: `Parabéns! Você completou a missão "${mission.nome || updatedMission.nome_missao}" e recebeu suas recompensas.`,
          tipo: 'SISTEMA',
          lida: false,
          data_registro: new Date().toISOString()
        });
      } catch (e) {
        console.error('[Gamification] Erro ao criar notificação:', e);
      }

      // 4. Verificar conquistas de "Missões Concluídas"
      try {
        await this.processAchievements(userId, 'MISSOES_CONCLUIDAS');
      } catch (e) {
        console.error('[Gamification] Erro ao processar conquistas:', e);
      }
    } else {
      if (updateError) {
        console.error(`[Gamification] Falha ao finalizar missão ${userMissionId} para usuário ${userId}:`, updateError);
      } else {
        console.warn(`[Gamification] Missão ${userMissionId} para usuário ${userId} não pôde ser finalizada (já concluída ou registro não encontrado).`);
      }
    }
  },

  /**
   * Verifica se todos os eventos de uma missão foram concluídos para finalizar a missão.
   */
  async checkMissionCompletion(userId: string, userMissionId: string, mission: any) {
    const { data: userEvents } = await supabase
      .from('missoes_user_eventos')
      .select('*')
      .eq('missao_user_id', userMissionId);

    const { data: targetEvents } = await supabase
      .from('missoes_eventos')
      .select('*')
      .eq('missao_id', mission.id);

    if (!targetEvents || targetEvents.length === 0) {
      // Missão sem sub-eventos vinculados: conclusão direta
      await this.finalizeMission(userId, userMissionId, mission);
      return;
    }

    // Calcular o progresso total
    let totalProgressSum = 0;
    let eventsCompleted = 0;

    for (const target of targetEvents) {
      const userEvent = userEvents?.find(ue => ue.evento_id === target.id);
      if (userEvent) {
        const meta = Number(target.meta_quantidade) || 1;
        const prog = Number(userEvent.progresso_atual) || 0;
        const eventProgress = Math.min(1, prog / meta);
        totalProgressSum += eventProgress;
        if (userEvent.concluido || prog >= meta) {
          eventsCompleted++;
        }
      }
    }

    const totalEvents = targetEvents.length;
    const progressValue = Number((totalProgressSum / totalEvents).toFixed(2));
    const missionCompleted = eventsCompleted >= totalEvents || progressValue >= 1;

    if (missionCompleted) {
      await this.finalizeMission(userId, userMissionId, mission);
    } else {
      // Apenas atualizar o percentual de progresso se não concluída
      await supabase
        .from('missoes_user')
        .update({ progresso: progressValue })
        .eq('id', userMissionId)
        .eq('concluida', false);
    }
  },

  /**
   * Concede a medalha vinculada à missão concluída.
   */
  async grantMedal(userId: string, missionId: string) {
    const { data: medal, error: medalError } = await supabase
      .from('medalhas')
      .select('*')
      .eq('missao_id', missionId)
      .maybeSingle();

    if (medalError) {
      console.error('[Gamification] Erro ao buscar medalha:', medalError);
      return;
    }

    if (medal) {
      const { error: grantError } = await supabase
        .from('medalhas_user')
        .upsert({
          usuario_id: userId,
          medalha_id: medal.id,
          missao_id: missionId,
          imagem_medalha: medal.imagem_medalha,
          conquistado_em: new Date().toISOString()
        }, { onConflict: 'usuario_id,medalha_id' });
      
      if (grantError) {
        console.error('[Gamification] Erro ao salvar medalha para o usuário:', grantError);
      } else {
        console.log(`[Gamification] Medalha "${medal.titulo}" concedida ao usuário ${userId}`);
      }
    }
  },

  /**
   * Aplica pontos, exp e loga no extrato.
   */
  async applyRewards(userId: string, mission: any) {
    console.log(`[Gamification] Aplicando recompensas da missão "${mission.nome || mission.nome_missao}" para o usuário ${userId}`);
    
    const pontosGanhos = Number(mission.pontos || 0);
    const expGanho = Number(mission.exp || 0);

    // 1. Atualizar Pontos em Usuarios atomicamente
    if (pontosGanhos > 0) {
      try {
        await pointsService.atomicAddUserPoints(userId, pontosGanhos);

        // Log extrato
        await supabase
          .from('extrato')
          .insert({
            usuario_id: userId,
            descricao: `Missão concluída: ${mission.nome || mission.nome_missao || 'Desafio Ganho'}`,
            pontos: pontosGanhos,
            entrada: true,
            data_registro: new Date().toISOString()
          });

        // Pontos de missão valem R$0,01/ponto no resgate de cashback (fonte != descarte).
        await pointsService.recordPointsLedgerEntry({
          userId,
          pontos: pontosGanhos,
          origem: 'MISSAO',
          valorUnitario: 0.01,
          referenciaId: mission.id || null
        });
      } catch (err) {
        console.error('[Gamification] Erro ao conceder pontos da missão:', err);
      }
    }

    // 2. Atualizar EXP em Usuarios_Ligas (ou criar se não existir)
    if (expGanho > 0) {
      try {
        await pointsService.updateUserXP(userId, expGanho);
      } catch (e) {
        console.error('[Gamification] Erro ao atualizar EXP via pointsService:', e);
        // Fallback manual garantindo consistência de XP e ranking_ligas
        const { data: userLeague } = await supabase
          .from('usuarios_ligas')
          .select('id, xp_total, liga_id')
          .eq('usuario_id', userId)
          .maybeSingle();

        let activeLigaId = userLeague?.liga_id;

        if (userLeague) {
          await supabase
            .from('usuarios_ligas')
            .update({ 
              xp_total: (Number(userLeague.xp_total) || 0) + expGanho,
              data_atualizacao: new Date().toISOString()
            })
            .eq('usuario_id', userId);
        } else {
          const { data: firstLeague } = await supabase.from('ligas').select('id').order('nivel').limit(1).single();
          if (firstLeague) {
            activeLigaId = firstLeague.id;
            await supabase.from('usuarios_ligas').insert({ 
              usuario_id: userId, 
              liga_id: firstLeague.id, 
              xp_total: expGanho,
              data_registro: new Date().toISOString(),
              data_atualizacao: new Date().toISOString()
            });
          }
        }

        // Sincroniza ranking_ligas periódico para não divergir o placar
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString().split('T')[0];
        
        const { data: rankingRecord } = await supabase
          .from('ranking_ligas')
          .select('id, xp_periodo')
          .eq('usuario_id', userId)
          .eq('periodo_inicio', firstDay)
          .eq('periodo_fim', lastDay)
          .maybeSingle();

        if (rankingRecord) {
          await supabase
            .from('ranking_ligas')
            .update({
              xp_periodo: (Number(rankingRecord.xp_periodo) || 0) + expGanho,
              data_atualizacao: new Date().toISOString()
            })
            .eq('id', rankingRecord.id);
        } else if (activeLigaId) {
          await supabase
            .from('ranking_ligas')
            .insert({
              usuario_id: userId,
              liga_id: activeLigaId,
              periodo_inicio: firstDay,
              periodo_fim: lastDay,
              xp_periodo: expGanho,
              data_registro: new Date().toISOString(),
              data_atualizacao: new Date().toISOString()
            });
        }
      }
    }
    
    console.log(`[Gamification] Recompensas aplicadas: ${pontosGanhos} pts, ${expGanho} exp.`);
  },

  /**
   * Processa conquistas globais baseadas em marcos (milestones).
   */
  async processAchievements(userId: string, tipoMarco: MilestoneType) {
    // 1. Buscar marcos ativos desse tipo
    const { data: activeAchievements } = await supabase
      .from('conquistas')
      .select('*')
      .eq('tipo_marco', tipoMarco)
      .eq('ativa', true);

    if (!activeAchievements || activeAchievements.length === 0) return;

    // 2. Obter valor atual do usuário para comparação
    let currentValue = 0;
    const { data: profile } = await supabase
      .from('usuarios')
      .select('qtd_descartes, pontos, saldo_pontos, qtd_kg_desc')
      .eq('id', userId)
      .single();
    
    if (!profile) return;

    switch (tipoMarco) {
      case 'DESCARTES':
        currentValue = profile.qtd_descartes || 0;
        break;
      case 'PONTOS':
        currentValue = profile.pontos || 0;
        break;
      case 'VOLUME':
        currentValue = profile.qtd_kg_desc || 0;
        break;
      case 'MISSOES_CONCLUIDAS':
        const { count } = await supabase
          .from('missoes_user')
          .select('id', { count: 'exact', head: true })
          .eq('usuario_id', userId)
          .eq('concluida', true);
        currentValue = count || 0;
        break;
    }

    // 3. Verificar cada conquista aplicável
    for (const achievement of activeAchievements) {
      if (currentValue >= achievement.valor_marco) {
        // Conceder conquista se o usuário ainda não tiver
        const { error } = await supabase
          .from('conquistas_user')
          .insert({
            usuario_id: userId,
            conquista_id: achievement.id,
            imagem_url: achievement.imagem_url,
            conquistado_em: new Date().toISOString()
          });
        
        // Se der erro de duplicidade (PK/Unique index), o Supabase rejeita e não fazemos nada (idempotente)
        if (!error) {
          console.log(`[Gamification] Usuário ${userId} desbloqueou conquista: ${achievement.titulo}`);
          
          // Criar Notificação de Conquista
          await supabase.from('notificacoes').insert({
            usuario_id: userId,
            titulo: 'Nova Conquista! 🏆',
            mensagem: `Você desbloqueou a conquista "${achievement.titulo}".`,
            tipo: 'SISTEMA',
            lida: false,
            data_registro: new Date().toISOString()
          });

          // Recompensa de pontos da conquista (atômica)
          if (achievement.pontos > 0) {
            try {
              await pointsService.atomicAddUserPoints(userId, achievement.pontos);

              await supabase.from('extrato').insert({
                usuario_id: userId,
                descricao: `Conquista desbloqueada: ${achievement.titulo}`,
                pontos: achievement.pontos,
                entrada: true,
                tipo_transacao: 'BONUS',
                data_registro: new Date().toISOString()
              });

              // Pontos de conquista valem R$0,01/ponto no resgate de cashback (fonte != descarte).
              await pointsService.recordPointsLedgerEntry({
                userId,
                pontos: achievement.pontos,
                origem: 'CONQUISTA',
                valorUnitario: 0.01,
                referenciaId: achievement.id || null
              });
            } catch (rewardErr) {
              console.error("[Gamification] Erro ao conceder pontos de conquista:", rewardErr);
            }
          }
        }
      }
    }
  },
  
  /**
   * Verifica e concede todas as conquistas pendentes para o usuário.
   */
  async checkAndAwardAchievements(userId: string) {
    console.log(`[Gamification] Sincronizando todas as conquistas para usuário ${userId}`);
    await this.processAchievements(userId, 'DESCARTES');
    await this.processAchievements(userId, 'PONTOS');
    await this.processAchievements(userId, 'VOLUME');
    await this.processAchievements(userId, 'MISSOES_CONCLUIDAS');
  },

  /**
   * Sincroniza e reavalia missões do usuário baseando-se no histórico de descartes.
   */
  async syncAndEvaluateMissionsForUser(userId: string) {
    try {
      const { data: discards } = await supabase
        .from('descartes')
        .select('*, descartes_materiais(*)')
        .eq('usuario_id', userId);

      if (!discards || discards.length === 0) return;

      for (const d of discards) {
        const totalKg = Number(d.kg_total || d.peso_total || 0);
        await this.processarEventoUsuario(userId, 'DESCARTE', {
          ecopontoId: d.ecoponto_id,
          amount: totalKg > 0 ? totalKg : 1,
          weightKg: totalKg
        });

        if (d.descartes_materiais && Array.isArray(d.descartes_materiais)) {
          for (const dm of d.descartes_materiais) {
            const mKg = Number(dm.gramas || 0) / 1000;
            await this.processarEventoUsuario(userId, 'MATERIAL', {
              materialId: dm.material_id,
              materialName: dm.material,
              ecopontoId: d.ecoponto_id,
              amount: mKg > 0 ? mKg : 1,
              weightKg: mKg
            });
          }
        }
      }
    } catch (err) {
      console.warn('[Gamification] Erro ao sincronizar missões do usuário:', err);
    }
  }
};
